import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Surface, Button, Alert, Input, Tabs } from '../../ui/components';
import { Users, EyeOff, TrendingUp, Building } from 'lucide-react';
import { authService } from '../../services/supabaseService';
import { OwenAvatar } from '../../components/Owen';

const LenderLoginPage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [nmlsId, setNmlsId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleDemoMode = () => {
    // Bypass auth for demo purposes
    navigate('/lender/marketplace');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await authService.signIn(email, password);
      navigate('/lender/marketplace');
    } catch (err) {
      setError(err.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await authService.signUp(email, password, {
        role: 'lender',
        company_name: companyName,
        nmls_id: nmlsId,
      });
      setSuccess('Account created! Please check your email to verify.');
      setTab(0);
    } catch (err) {
      setError(err.message || 'Sign up failed');
    } finally {
      setLoading(false);
    }
  };

  const FeatureRow = ({ icon: Icon, title, desc }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-3)', padding: 'var(--lk-space-3)', borderRadius: 'var(--lk-radius-md)', background: 'rgba(255,255,255,0.1)' }}>
      <div style={{ width: 44, height: 44, borderRadius: 'var(--lk-radius-md)', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={24} />
      </div>
      <div style={{ textAlign: 'left' }}>
        <div style={{ fontWeight: 600, fontSize: 'var(--lk-text-sm)' }}>{title}</div>
        <div style={{ fontSize: 'var(--lk-text-xs)', opacity: 0.8 }}>{desc}</div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
      {/* Left Side */}
      <div style={{
        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 'var(--lk-space-10)',
      }}>
        <div style={{ maxWidth: 500, textAlign: 'center' }}>
          <div style={{ marginBottom: 'var(--lk-space-6)' }}>
            <OwenAvatar size="xlarge" showTooltip={false} />
          </div>
          <h1 style={{ fontSize: 'var(--lk-text-4xl)', fontWeight: 700, marginBottom: 'var(--lk-space-3)', letterSpacing: '-0.02em' }}>
            Lender Marketplace
          </h1>
          <p style={{ fontSize: 'var(--lk-text-lg)', opacity: 0.9, marginBottom: 'var(--lk-space-8)' }}>
            Access pre-qualified mortgage leads with complete anonymity. Connect with ready borrowers.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-3)' }}>
            <FeatureRow icon={Users} title="Pre-Qualified Leads" desc="Access borrowers ready to close" />
            <FeatureRow icon={EyeOff} title="Anonymous Bidding" desc="Compete on rates, not relationships" />
            <FeatureRow icon={TrendingUp} title="Real-Time Analytics" desc="Track your pipeline and conversions" />
          </div>
        </div>
      </div>

      {/* Right Side */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 'var(--lk-space-10)',
        background: 'white',
      }}>
        <div style={{ width: '100%', maxWidth: 440 }}>
          <div style={{ textAlign: 'center', marginBottom: 'var(--lk-space-6)' }}>
            <h2 style={{ fontWeight: 700, marginBottom: 4 }}>Lender Access</h2>
            <p style={{ color: 'var(--lk-text-tertiary)', margin: 0 }}>Sign in or create a free account</p>
          </div>

          <Tabs
            tabs={[
              { id: 0, label: 'Sign In' },
              { id: 1, label: 'Sign Up Free' },
            ]}
            activeTab={tab}
            onChange={setTab}
          />

          <div style={{ marginTop: 'var(--lk-space-4)' }}>
            {error && (
              <Alert variant="error" style={{ marginBottom: 'var(--lk-space-3)' }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert variant="success" style={{ marginBottom: 'var(--lk-space-3)' }}>
                {success}
              </Alert>
            )}

            {tab === 0 ? (
              <form onSubmit={handleLogin}>
                <Input
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="lender@example.com"
                  required
                />
                <Input
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  style={{ marginTop: 'var(--lk-space-3)' }}
                />
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  loading={loading}
                  style={{ width: '100%', marginTop: 'var(--lk-space-4)' }}
                >
                  Sign In
                </Button>
              </form>
            ) : (
              <form onSubmit={handleSignUp}>
                <Input
                  label="Company Name"
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Your Lending Company"
                  required
                />
                <Input
                  label="NMLS ID"
                  type="text"
                  value={nmlsId}
                  onChange={(e) => setNmlsId(e.target.value)}
                  placeholder="123456"
                  required
                  style={{ marginTop: 'var(--lk-space-3)' }}
                />
                <hr style={{ margin: 'var(--lk-space-4) 0', border: 'none', borderTop: '1px solid var(--lk-border-subtle)' }} />
                <Input
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="lender@example.com"
                  required
                />
                <Input
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 8 characters"
                  required
                  style={{ marginTop: 'var(--lk-space-3)' }}
                />
                <div style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-quaternary)', marginTop: 4 }}>Minimum 8 characters</div>

                <Button
                  type="submit"
                  variant="secondary"
                  size="lg"
                  loading={loading}
                  style={{ width: '100%', marginTop: 'var(--lk-space-4)' }}
                >
                  Create Free Account
                </Button>
                <p style={{ color: 'var(--lk-text-quaternary)', textAlign: 'center', fontSize: 'var(--lk-text-xs)', marginTop: 'var(--lk-space-3)' }}>
                  By signing up, you agree to our Terms of Service and Privacy Policy
                </p>
              </form>
            )}
          </div>

          <Surface variant="muted" padding="var(--lk-space-3)" style={{ marginTop: 'var(--lk-space-5)', textAlign: 'center' }}>
            <div style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-quaternary)', marginBottom: 'var(--lk-space-2)' }}>
              For demonstration purposes
            </div>
            <Button
              variant="secondary"
              style={{ width: '100%', borderStyle: 'dashed' }}
              onClick={handleDemoMode}
            >
              Enter Demo Mode
            </Button>
          </Surface>

          <p style={{ color: 'var(--lk-text-quaternary)', textAlign: 'center', fontSize: 'var(--lk-text-xs)', marginTop: 'var(--lk-space-4)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
            <Building size={12} />
            Secure lender marketplace powered by Owen AI
          </p>
        </div>
      </div>
    </div>
  );
};

export default LenderLoginPage;
